import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import {
  createSavedView,
  deleteSavedView,
  fetchSavedViews,
  nextSavedViewPosition,
  updateSavedView,
} from './queries';
import type { SavedViewInput } from './types';

/**
 * Server state for saved views. One list per person covers every project:
 * the rows are small, personal, and filtered client-side by project.
 */

export const savedViewKeys = {
  all: ['savedViews'] as const,
  list: () => [...savedViewKeys.all, 'list'] as const,
};

/** The signed-in user's id, or an empty string when access is not approved. */
function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useSavedViews() {
  const { client } = useAuth();
  return useQuery({
    queryKey: savedViewKeys.list(),
    queryFn: () => fetchSavedViews(client),
    retry: false,
  });
}

export function useCreateSavedView() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const ownerId = useActorId();
  return useMutation({
    mutationFn: async (input: SavedViewInput) => {
      const existing = await fetchSavedViews(client);
      return createSavedView(client, {
        ...input,
        ownerId,
        position: nextSavedViewPosition(existing),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedViewKeys.list() });
    },
  });
}

export function useUpdateSavedView() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ viewId, input }: { viewId: string; input: Omit<SavedViewInput, 'projectId'> }) =>
      updateSavedView(client, viewId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedViewKeys.list() });
    },
  });
}

export function useDeleteSavedView() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (viewId: string) => deleteSavedView(client, viewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: savedViewKeys.list() });
    },
  });
}
